import { mediaUrl } from "./media";

const wrap = (ctx, text, x, y, maxWidth, lineHeight, maxLines = 99) => {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  let line = ""; let row = 0;
  words.forEach((word) => {
    const candidate = line ? `${line} ${word}` : word;
    if (ctx.measureText(candidate).width > maxWidth && line) {
      if (row < maxLines) ctx.fillText(line, x, y + row * lineHeight);
      row += 1; line = word;
    } else line = candidate;
  });
  if (line && row < maxLines) ctx.fillText(line, x, y + row * lineHeight);
  return Math.min(row + 1, maxLines) * lineHeight;
};

const loadImage = (url) => new Promise((resolve) => {
  if (!url) return resolve(null);
  const image = new Image(); image.crossOrigin = "anonymous";
  image.onload = () => resolve(image); image.onerror = () => resolve(null); image.src = url;
});

const asBlob = (canvas) => new Promise((resolve, reject) => canvas.toBlob(
  (blob) => blob ? resolve(blob) : reject(new Error("Recipe card could not be rendered")),
  "image/png", 0.95,
));

export async function createAndShareRecipeCard({ meal, ingredients, preparation, labels, fallbackText }) {
  const width = 1080; const imageHeight = 390;
  const ingredientRows = Math.max(ingredients.length, 1);
  const stepRows = Math.max(preparation.length, 1);
  const height = Math.max(1180, 760 + ingredientRows * 54 + stepRows * 82);
  const canvas = document.createElement("canvas"); canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext("2d");
  const gradient = ctx.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, "#07151c"); gradient.addColorStop(1, "#111820");
  ctx.fillStyle = gradient; ctx.fillRect(0, 0, width, height);
  ctx.fillStyle = "#00e5ff"; ctx.fillRect(0, 0, 14, height);

  ctx.fillStyle = "#00e5ff"; ctx.font = "700 25px Arial";
  ctx.fillText("PERFORMANCE CARE", 64, 62);
  ctx.fillStyle = "#f4f8fa"; ctx.font = "700 52px Arial";
  wrap(ctx, meal.name, 64, 132, 930, 60, 2);

  const image = await loadImage(mediaUrl(meal.image_url));
  ctx.save(); ctx.beginPath(); ctx.roundRect(64, 210, 952, imageHeight, 28); ctx.clip();
  if (image) {
    const ratio = Math.max(952 / image.width, imageHeight / image.height);
    const drawWidth = image.width * ratio; const drawHeight = image.height * ratio;
    ctx.drawImage(image, 64 + (952 - drawWidth) / 2, 210 + (imageHeight - drawHeight) / 2, drawWidth, drawHeight);
  } else {
    const fallback = ctx.createLinearGradient(64, 210, 1016, 600);
    fallback.addColorStop(0, "#10323d"); fallback.addColorStop(1, "#18222a");
    ctx.fillStyle = fallback; ctx.fillRect(64, 210, 952, imageHeight);
    ctx.fillStyle = "rgba(0,229,255,.55)"; ctx.font = "700 100px Arial";
    ctx.fillText("PC", 435, 445);
  }
  ctx.restore();

  const nutrition = meal.nutrition || {};
  const metrics = [
    [labels.kcal, Math.round(nutrition.kcal || 0)],
    [labels.protein, `${Math.round(nutrition.protein || 0)} g`],
    [labels.carbs, `${Math.round(nutrition.carbohydrates || 0)} g`],
    [labels.fat, `${Math.round(nutrition.fat || 0)} g`],
  ];
  metrics.forEach(([label, value], index) => {
    const x = 64 + index * 238;
    ctx.fillStyle = "#15242c"; ctx.beginPath(); ctx.roundRect(x, 632, 220, 92, 18); ctx.fill();
    ctx.fillStyle = "#8fa3ad"; ctx.font = "600 20px Arial"; ctx.fillText(label, x + 18, 662);
    ctx.fillStyle = "#ffffff"; ctx.font = "700 30px Arial"; ctx.fillText(String(value), x + 18, 704);
  });

  let y = 790;
  ctx.fillStyle = "#00e5ff"; ctx.font = "700 24px Arial"; ctx.fillText(labels.cookingAmount, 64, y); y += 42;
  ctx.fillStyle = "#f4f8fa"; ctx.font = "600 25px Arial";
  ingredients.forEach((row) => {
    ctx.fillText(`• ${row.name}`, 76, y); ctx.textAlign = "right";
    ctx.fillText(`${row.quantity} ${row.unit}`, 1000, y); ctx.textAlign = "left"; y += 50;
  });
  y += 24; ctx.fillStyle = "#00e5ff"; ctx.font = "700 24px Arial";
  ctx.fillText(labels.preparation, 64, y); y += 42;
  ctx.fillStyle = "#dbe5e9"; ctx.font = "400 24px Arial";
  preparation.forEach((step, index) => {
    ctx.fillStyle = "#00e5ff"; ctx.fillText(`${index + 1}.`, 70, y);
    ctx.fillStyle = "#dbe5e9"; y += wrap(ctx, step, 112, y, 888, 32, 4) + 18;
  });
  ctx.fillStyle = "#667985"; ctx.font = "400 18px Arial";
  ctx.fillText(labels.footer, 64, height - 38);

  const blob = await asBlob(canvas);
  const safeName = String(meal.name || "recipe").replace(/[^a-z0-9äöüß_-]+/gi, "-").replace(/^-|-$/g, "");
  const file = new File([blob], `${safeName || "recipe"}.png`, { type: "image/png" });
  if (navigator.share && navigator.canShare?.({ files: [file] })) {
    await navigator.share({ title: meal.name, text: fallbackText, files: [file] });
    return "shared-file";
  }
  const url = URL.createObjectURL(blob); const anchor = document.createElement("a");
  anchor.href = url; anchor.download = file.name; document.body.appendChild(anchor); anchor.click(); anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  return "downloaded";
}
